import type { AddressAutofillRetrieveResponse } from "@mapbox/search-js-core";
import type { ReactNode } from "react";
import type {
	Control,
	UseFormGetValues,
	UseFormSetValue,
} from "react-hook-form";

import { AddressAutofill } from "@mapbox/search-js-react";
import { useCallback, useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";

import { getMapboxAccessToken } from "@/features/location-management/map/mapbox-constants";
import { mapAddressAutofillRetrieveToForm } from "@/utils/address-autofill";
import {
	buildAddressQuery,
	forwardGeocode,
	reverseGeocode,
} from "@/utils/reverse-geocode";
import { getTimezoneFromCoordinates } from "@/utils/timezone";

/** Fields shared with dashboard LocationFormProps for autofill / geocode. */
export type LocationGeocodeFormValues = {
	city: string;
	latitude: string;
	longitude: string;
	name: string;
	postalCode: string;
	stateProvince: string;
	street1: string;
	street2: string;
	timezone: string;
};

const FIELD_OPTIONS = {
	shouldDirty: true,
	shouldValidate: true,
} as const;

type UseLocationAddressGeocodeParams = {
	control: Control<LocationGeocodeFormValues>;
	enabled?: boolean;
	getValues: UseFormGetValues<LocationGeocodeFormValues>;
	setValue: UseFormSetValue<LocationGeocodeFormValues>;
};

/** True when the event target is inside Mapbox's portaled suggestion listbox. */
export const isMapboxAutofillListboxTarget = (
	target: EventTarget | null,
): boolean => {
	if (!(target instanceof Node)) return false;

	let node: Node | null = target;
	while (node) {
		if (node instanceof Element && node.localName === "mapbox-search-listbox") {
			return true;
		}
		node = node.parentNode;
	}
	return false;
};

/**
 * Mapbox mounts `mapbox-search-listbox` on document.body (outside the dialog).
 * Return false so React Aria keeps the modal open when clicking a suggestion.
 */
export const keepOpenForMapboxAutofill = (element: Element): boolean =>
	!isMapboxAutofillListboxTarget(element);

/**
 * Mapbox Address Autofill + forward/reverse geocode wiring for location forms.
 * Mirrors apps/dashboard location-details tab (react-hook-form setValue/getValues).
 */
export const useLocationAddressGeocode = ({
	control,
	enabled = true,
	getValues,
	setValue,
}: UseLocationAddressGeocodeParams) => {
	const mapboxAccessToken = getMapboxAccessToken();
	const geocodeAbortRef = useRef<AbortController | null>(null);
	const suppressAddressGeocodeRef = useRef(false);
	const lastGeocodedQueryRef = useRef<null | string>(null);

	const watchedStreet = useWatch({ control, name: "street1" });
	const watchedCity = useWatch({ control, name: "city" });
	const watchedState = useWatch({ control, name: "stateProvince" });

	const syncCoordinatesToForm = useCallback(
		(coordinates: [number, number]) => {
			const [lng, lat] = coordinates;
			setValue("latitude", String(lat), FIELD_OPTIONS);
			setValue("longitude", String(lng), FIELD_OPTIONS);

			const timezone = getTimezoneFromCoordinates(lat, lng);
			if (timezone) {
				setValue("timezone", timezone, FIELD_OPTIONS);
			}
		},
		[setValue],
	);

	const applyAutofillAddress = useCallback(
		(address: ReturnType<typeof mapAddressAutofillRetrieveToForm>) => {
			if (address.coordinates) {
				syncCoordinatesToForm(address.coordinates);
			}
			if (address.street1 !== undefined) {
				setValue("street1", address.street1, FIELD_OPTIONS);
			}
			if (address.city !== undefined) {
				setValue("city", address.city, FIELD_OPTIONS);
			}
			if (address.stateProvince !== undefined) {
				setValue("stateProvince", address.stateProvince, FIELD_OPTIONS);
			}
			if (address.postalCode !== undefined) {
				setValue("postalCode", address.postalCode, FIELD_OPTIONS);
			}

			const query = buildAddressQuery({
				city: address.city ?? getValues("city"),
				postalCode: address.postalCode ?? getValues("postalCode"),
				stateProvince: address.stateProvince ?? getValues("stateProvince"),
				street1: address.street1 ?? getValues("street1"),
			});

			if (address.coordinates) {
				lastGeocodedQueryRef.current = query ?? null;
			}

			if (address.coordinates || !mapboxAccessToken) {
				return;
			}

			if (!query) {
				return;
			}

			geocodeAbortRef.current?.abort();
			const abortController = new AbortController();
			geocodeAbortRef.current = abortController;

			void forwardGeocode({
				accessToken: mapboxAccessToken,
				query,
				signal: abortController.signal,
			}).then((result) => {
				if (!result || abortController.signal.aborted) return;

				lastGeocodedQueryRef.current = query;
				syncCoordinatesToForm(result.coordinates);

				if (result.address?.postalCode) {
					setValue("postalCode", result.address.postalCode, FIELD_OPTIONS);
				}
			});
		},
		[getValues, mapboxAccessToken, setValue, syncCoordinatesToForm],
	);

	const handleAddressAutofillRetrieve = useCallback(
		(response: AddressAutofillRetrieveResponse) => {
			if (!enabled) return;

			geocodeAbortRef.current?.abort();
			applyAutofillAddress(mapAddressAutofillRetrieveToForm(response));
		},
		[applyAutofillAddress, enabled],
	);

	const reverseGeocodeAt = useCallback(
		(coordinates: [number, number]) => {
			if (!enabled) return;

			suppressAddressGeocodeRef.current = true;
			syncCoordinatesToForm(coordinates);

			geocodeAbortRef.current?.abort();
			const abortController = new AbortController();
			geocodeAbortRef.current = abortController;

			void reverseGeocode({
				coordinates,
				signal: abortController.signal,
			})
				.then((address) => {
					if (!address || abortController.signal.aborted) return;

					if (address.street1 !== undefined) {
						setValue("street1", address.street1, FIELD_OPTIONS);
					}
					if (address.city !== undefined) {
						setValue("city", address.city, FIELD_OPTIONS);
					}
					if (address.stateProvince !== undefined) {
						setValue("stateProvince", address.stateProvince, FIELD_OPTIONS);
					}
					if (address.postalCode !== undefined) {
						setValue("postalCode", address.postalCode, FIELD_OPTIONS);
					}
				})
				.finally(() => {
					queueMicrotask(() => {
						suppressAddressGeocodeRef.current = false;
						lastGeocodedQueryRef.current =
							buildAddressQuery({
								city: getValues("city"),
								postalCode: getValues("postalCode"),
								stateProvince: getValues("stateProvince"),
								street1: getValues("street1"),
							}) ?? null;
					});
				});
		},
		[enabled, getValues, setValue, syncCoordinatesToForm],
	);

	const syncAddressQueryToCoordinates = useCallback(
		(query: string) => {
			if (!enabled || !mapboxAccessToken || suppressAddressGeocodeRef.current) {
				return;
			}
			if (query === lastGeocodedQueryRef.current) {
				return;
			}

			geocodeAbortRef.current?.abort();
			const abortController = new AbortController();
			geocodeAbortRef.current = abortController;

			void forwardGeocode({
				accessToken: mapboxAccessToken,
				query,
				signal: abortController.signal,
			}).then((result) => {
				if (!result || abortController.signal.aborted) return;

				lastGeocodedQueryRef.current = query;
				syncCoordinatesToForm(result.coordinates);

				if (result.address?.postalCode && !getValues("postalCode")?.trim()) {
					setValue("postalCode", result.address.postalCode, FIELD_OPTIONS);
				}
			});
		},
		[enabled, getValues, mapboxAccessToken, setValue, syncCoordinatesToForm],
	);

	useEffect(() => {
		if (!enabled || !mapboxAccessToken) return;
		if (suppressAddressGeocodeRef.current) return;

		const query = buildAddressQuery({
			city: watchedCity,
			postalCode: getValues("postalCode"),
			stateProvince: watchedState,
			street1: watchedStreet,
		});
		if (!query || !watchedStreet?.trim() || !watchedCity?.trim()) {
			return;
		}
		if (query === lastGeocodedQueryRef.current) {
			return;
		}

		const timer = window.setTimeout(() => {
			syncAddressQueryToCoordinates(query);
		}, 500);

		return () => window.clearTimeout(timer);
	}, [
		enabled,
		getValues,
		mapboxAccessToken,
		syncAddressQueryToCoordinates,
		watchedCity,
		watchedState,
		watchedStreet,
	]);

	useEffect(() => {
		return () => {
			geocodeAbortRef.current?.abort();
		};
	}, []);

	// React Aria modals call ariaHideOutside with shouldUseInert, which marks
	// every body child outside the dialog as inert — including Mapbox's
	// portaled `mapbox-search-listbox`. Clicks never reach suggestions (keyboard
	// Enter still works because focus stays on the input inside the modal).
	// Tag the listbox as a RAC top layer and clear inert when it mounts.
	useEffect(() => {
		if (!enabled || !mapboxAccessToken) return;

		const markListboxInteractive = (node: Node) => {
			if (!(node instanceof HTMLElement)) return;
			if (node.localName !== "mapbox-search-listbox") return;

			node.dataset.reactAriaTopLayer = "true";
			node.inert = false;
			node.removeAttribute("aria-hidden");
		};

		const scan = (root: ParentNode) => {
			if (root instanceof Element) {
				markListboxInteractive(root);
			}
			root
				.querySelectorAll?.("mapbox-search-listbox")
				.forEach((el) => markListboxInteractive(el));
		};

		scan(document.body);

		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				for (const node of mutation.addedNodes) {
					if (node instanceof Element || node instanceof DocumentFragment) {
						scan(node);
					}
				}
				// RAC may set inert after insert; undo on attribute changes too.
				if (
					mutation.type === "attributes" &&
					mutation.target instanceof Element
				) {
					markListboxInteractive(mutation.target);
				}
			}
		});

		observer.observe(document.body, {
			attributeFilter: ["inert", "aria-hidden"],
			attributes: true,
			childList: true,
			subtree: true,
		});

		return () => observer.disconnect();
	}, [enabled, mapboxAccessToken]);

	// Keep focus on the street input when clicking a portaled suggestion so
	// Mapbox does not hide the list on blur before the click lands.
	useEffect(() => {
		if (!enabled || !mapboxAccessToken) return;

		const preventBlurOnSuggestionPointer = (event: Event) => {
			if (isMapboxAutofillListboxTarget(event.target)) {
				event.preventDefault();
			}
		};

		document.addEventListener(
			"pointerdown",
			preventBlurOnSuggestionPointer,
			true,
		);
		document.addEventListener(
			"mousedown",
			preventBlurOnSuggestionPointer,
			true,
		);
		return () => {
			document.removeEventListener(
				"pointerdown",
				preventBlurOnSuggestionPointer,
				true,
			);
			document.removeEventListener(
				"mousedown",
				preventBlurOnSuggestionPointer,
				true,
			);
		};
	}, [enabled, mapboxAccessToken]);

	const wrapAddressFields = useCallback(
		(children: ReactNode): ReactNode => {
			if (!enabled || !mapboxAccessToken) return children;

			return (
				<AddressAutofill
					accessToken={mapboxAccessToken}
					onRetrieve={handleAddressAutofillRetrieve}
					options={{
						language:
							typeof navigator !== "undefined" ? navigator.language : "en",
					}}
				>
					{children}
				</AddressAutofill>
			);
		},
		[enabled, handleAddressAutofillRetrieve, mapboxAccessToken],
	);

	return {
		mapboxAccessToken,
		reverseGeocodeAt,
		shouldCloseOnInteractOutside: keepOpenForMapboxAutofill,
		syncCoordinatesToForm,
		wrapAddressFields,
	};
};
